'use client';
import { DriverStrip, SportStrip } from '@/components/molecules/Sports/SportStrip';
import type { Details as Team } from '@/components/molecules/Sports/SportStrip';
import dayjs from 'dayjs';
import timeZoneExtension from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import styles from './sportschedule.module.scss';
import { useMemo, useState } from 'react';
import { Race } from '@/packages/sxm/sports/races/RacesResponse';
import { DateOption, DateRange, GameList, RaceProps } from './interfaces';
import filters from './league.config';
import { DynamicTableRow } from '@/components/molecules/DynamicTable/DynamicTable';
import SXMLink from '@/shared-components/SXMLink';
import NoGames from './NoGames';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Option } from '@/ui-components/atoms/Inputs/Select/Option';
import { OptionGroup } from '@/ui-components/atoms/Inputs/Select/OptionGroup';
import { Select } from '@/ui-components/atoms/Inputs/Select/Select';
import { formatSatelliteChannel, renderChannelLink } from './channelCells';
import { buildListenOptions, resolveChannel, toChannelLabel } from '@/packages/sxm/sports/channelWaterfall';
import { useViewerTimeZone } from '@/hooks/useViewerTimeZone';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timeZoneExtension);
dayjs.extend(isBetween);

export default function RaceSchedule({
    renderedAt,
    leagueAbbr,
    remind_me_logo,
    remind_me_title,
    reminder_url,
    races,
    playerUrl,
}: RaceProps) {
    const timeZone = useViewerTimeZone();

    const dateOptions = useMemo<DateOption[]>(() => {
        const today = dayjs(renderedAt).tz(timeZone);
        const ranges: string[] = [];
        const dateOptions: DateOption[] = [];

        races?.forEach((race: Race) => {
            if (race.seriesName) {
                const filterData = filters[leagueAbbr] || filters.default;

                const raceDate = dayjs(race.startDateTime).tz(timeZone);
                if (today.isAfter(race.endDateTime)) {
                    return;
                }

                let start = raceDate.startOf('day');
                let end = raceDate.endOf('day');
                let label = start.isSame(today, 'day') ? start.format('[Today], MMMM D') : start.format('dddd, MMMM D');

                if (filterData.schedule === 'monthly') {
                    start = raceDate.startOf('month');
                    end = raceDate.endOf('month');
                    label = start.format('MMMM');
                } else if (filterData.schedule === 'weekly') {
                    if (filterData.weekStart) {
                        if (start.day() < filterData.weekStart) {
                            start = start.day(-(filterData.weekStart - 1));
                        } else if (start.day() > filterData.weekStart) {
                            start = start.startOf('week');
                            start = start.day(filterData.weekStart);
                        }
                    }

                    start = start.startOf('day');
                    end = start.clone().add(6, 'day').endOf('day');
                    const endLabel = start.isSame(end, 'month') ? end.format('D') : end.format('MMM D');
                    label = start.format(`MMM D [- ${endLabel}]`);
                }

                const rangeIndex = `${start}${end}`;

                if (ranges.indexOf(rangeIndex) === -1) {
                    ranges.push(rangeIndex);

                    dateOptions.push({
                        range: {
                            start: start,
                            end: end,
                        },
                        value: label,
                        label: label,
                    });
                }
            }
        });

        return dateOptions;
    }, [leagueAbbr, races, renderedAt, timeZone]);

    const [selectedDateRange, setSelectedDateRange] = useState<DateRange>();
    const dateRange = useMemo(() => {
        const now = dayjs(renderedAt).tz(timeZone);

        return selectedDateRange ?? dateOptions[0]?.range ?? { start: now, end: now };
    }, [dateOptions, renderedAt, selectedDateRange, timeZone]);

    const [raceDates, dates] = useMemo<[GameList, string[]]>(() => {
        const today = dayjs(renderedAt).tz(timeZone);
        const raceDates: GameList = {};
        const dates: string[] = [];

        if (races) {
            for (let i = 0; i < races.length; i += 1) {
                const race = races[i] as Race;
                const index = race.startDateTime || '';
                const raceDate = dayjs(race.startDateTime).tz(timeZone);

                if (today.isAfter(race.endDateTime) || !raceDate.isBetween(dateRange.start, dateRange.end)) {
                    continue;
                }

                if (!raceDates[index]) {
                    const dateStr = raceDate.format('dddd, MMMM D • h:mm A z');
                    raceDates[index] = [
                        <Text
                            key={`header-${index}`}
                            variant="text-small"
                            tag="h3"
                            className={styles.dateTitle}
                            dataCy="components-contextual-sports-date-title"
                        >
                            {dateStr}
                        </Text>,
                    ];
                    dates.push(index);
                }

                const leagueRace: Team = {
                    logo: race.homeLogo,
                    pageUrl: race.homeUrl,
                    playerUrl: playerUrl,
                    name: race.raceOrDriverName || race.seriesName || '',
                    location: race.venueOrTeamName || '',
                    channel: toChannelLabel(resolveChannel(race, 'national')),
                };

                const moreWays: DynamicTableRow[] = buildListenOptions(race).map((option) => ({
                    rowLabel: option.label,
                    items: [
                        formatSatelliteChannel(option.channel),
                        renderChannelLink(
                            option.deepLink,
                            option.internetChannel,
                            `Listen to ${race.raceOrDriverName} on channel ${option.channel}`,
                        ),
                    ],
                }));

                const isLive = today.isBetween(race.startDateTime, race.endDateTime);
                const drivers =
                    race.raceDrivers && race.raceDrivers.length > 0 ? <DriverStrip drivers={race.raceDrivers} /> : '';
                const isNascar = leagueAbbr.toLowerCase() === 'nascar';

                raceDates[index].push(
                    <li
                        key={`race-${race.startDateTime}-${race.raceOrDriverName ?? race.seriesName ?? ''}-${race.venueOrTeamName ?? ''}`}
                    >
                        <SportStrip
                            league={leagueRace}
                            moreWays={moreWays}
                            isLive={isLive}
                            subStrip={drivers}
                            tabletContentLayout={isNascar ? 'column' : 'row'}
                            ariaLabel={`Listen to ${race.raceOrDriverName} on channel ${race.nationalStream}`}
                        />
                    </li>,
                );
            }
        }

        return [raceDates, dates];
    }, [races, dateRange.start, dateRange.end, leagueAbbr, playerUrl, renderedAt, timeZone]);

    function dateSelectHandler(element: HTMLInputElement, { data }: { data?: DateRange }) {
        if (data) {
            setSelectedDateRange(data);
        }
    }

    if (dates.length > 0) {
        return (
            <Grid container className={styles.scheduleContainer}>
                <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                    <div className={styles.filterBar}>
                        <Select<DateRange>
                            label="filter races by date"
                            type="bar"
                            value={dateOptions[0].value}
                            name="dateFilter"
                            onChange={dateSelectHandler}
                            className={styles.filterBarSelect}
                        >
                            <OptionGroup>
                                {dateOptions.map((option, i) => (
                                    <Option key={i} object={option.range} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                                <Option value="" disableOption className={styles.moreGames}>
                                    More events will be added as information becomes available.
                                </Option>
                            </OptionGroup>
                        </Select>
                    </div>

                    {dates.sort().map((index) => (
                        <ul key={index} className={styles.listStylings}>
                            {raceDates[index]}
                        </ul>
                    ))}

                    <StyledContainer paddingTop={48}>
                        <NoGames
                            logo={remind_me_logo}
                            logoSize="small"
                            title={remind_me_title || 'Never Miss a Moment'}
                        >
                            <SXMLink href={reminder_url} dataCy="contextualcomponent-race-addtocal-link">
                                Add to calendar
                            </SXMLink>
                        </NoGames>
                    </StyledContainer>
                </Grid>
            </Grid>
        );
    }

    return <></>;
}
